<template>
  <div
    class="movie-player-control-area"
    :class="{
      'full-mode': isViewModeFull,
      'compare-mode': isViewModeComp,
    }"
  >
    <div class="corner-triangle-label" v-if="isViewModeComp" :style="{opacity: cornerLabelOpacity}"></div>
    <div class="row1">
      <div class="play-speed-control" v-if="isSizeModeGteLg">
        <div class="lbl-container">
          <label class="lbl">再生速度</label>
        </div>
        <div class="speed-btn-container">
          <div
            class="btn btn-secondary speed-btn"
            v-for="(e, i) in playSpeeds"
            :key="i"
            :class="{ active: e === playSpeed }"
            @click="changePlaySpeed(e)"
          >
          </div>
        </div>
      </div>
      <div class="playback-control">
        <div class="ib vm control-btn" @click="seekBackwardSixtySec" v-if="!isViewModeOther">
          <img :src="'/static/img/60s_back.png'" class="sec-control-btn">
        </div>
        <div class="ib vm control-btn" @click="seekBackwardOneSec" v-if="!isViewModeOther">
          <img :src="'/static/img/1s_back.png'" class="sec-control-btn">
        </div>
        <div class="ib vm control-btn" @click="tickBackward">
          <i class="fas fa-backward"></i>
        </div>
        <div class="ib vm control-btn" @click="play" v-if="isPaused">
          <i class="fa-2x far fa-play-circle" :class="{'play-backward': isPlayBackward}"></i>
        </div>
        <div class="ib vm control-btn pause" @click="pause" v-if="isPlaying">
          <i class="fa-2x far fa-pause-circle"></i>
        </div>
        <div class="ib vm control-btn" @click="tickForward">
          <i class="fas fa-forward"></i>
        </div>
        <div class="ib vm control-btn" @click="seekForwardOneSec">
          <img :src="'/static/img/1s_foward.png'" class="sec-control-btn" v-if="!isViewModeOther">
        </div>
        <div class="ib vm control-btn" @click="seekForwardSixtySec">
          <img :src="'/static/img/60s_foward.png'" class="sec-control-btn" v-if="!isViewModeOther">
        </div>
      </div>
      <div class="volume-control" v-if="isSizeModeGteLg">
        <div class="ib vm lbl volume-off" @click="volumeOff">
          <i class="fas fa-volume-off"></i>
        </div>
        <div class="ib vm bar">
          <input
            type="range"
            class="slider"
            v-model.number="state.volume.val"
            @change="volumeChange"
            :min="volumeMin"
            :max="volumeMax"
            :step="volumeStep"
          >
        </div>
        <div class="ib vm lbl volume-up" @click="volumeUp">
          <i class="fas fa-volume-up"></i>
        </div>
      </div>
      <div class="custom-control custom-checkbox backward-checkbox">
        <input
          type="checkbox"
          class="custom-control-input"
          :id="`is-playbackward-${controlAreaId}`"
          @click="pause"
          :checked="isPlayBackward"
          @change="onIsPlayBackwardChange($event)"
        >
        <label class="custom-control-label" :for="`is-playbackward-${controlAreaId}`">
          逆再生
        </label>
      </div>
    </div>
    <div class="row2" v-if="!isViewModeOther">
      <div class="seek-bar">
        <span class="lbl time-start">{{myCurrentTimeDisp}}</span>
        <input
          type="range"
          class="slider"
          :value="seekCurrent"
          @input="seekChange($event)"
          :min="seekMin"
          :max="seekMax"
          :step="seekStep"
          ref="refSeekbarInput"
        >
        <span class="lbl time-end">{{endTimeDisp}}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  reactive,
  computed,
  ref,
} from 'vue';
import type { ViewMode, SizeMode } from 'src/models/moviePlayer';

interface VolumeState {
  val: number;
}
interface MoviePlayerControlAreaState {
  volume: VolumeState;
}

// withDefaultsでdefaultを与えても、TS上optional(?)でないとrequired:trueのままになるVueの仕様
// https://github.com/vuejs/core/blob/v3.5.35/packages/compiler-sfc/src/script/resolveType.ts (required: !e.optional)
// controlAreaId/viewModeは全呼び出し元が渡しているため必須のまま。isPlayBackward/isPlayingはdefaultなしのため必須のまま
interface Props {
  controlAreaId: string;
  viewMode: ViewMode;
  sizeMode?: SizeMode;
  currentTimeDisp?: string;
  startTimeDisp?: string;
  endTimeDisp?: string;
  seekMin?: number;
  seekMax?: number;
  seekStep?: number;
  seekCurrent?: number;
  cornerLabelOpacity?: number;
  playSpeed?: number;
  isPlayBackward: boolean;
  isPlaying: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  sizeMode: 'lg',
  currentTimeDisp: '',
  startTimeDisp: '',
  endTimeDisp: '',
  seekMin: 0,
  seekMax: 0,
  seekStep: 1,
  seekCurrent: 0,
  cornerLabelOpacity: 1.0,
  playSpeed: 1.0,
});

const emit = defineEmits<{
  (e: 'play'): void;
  (e: 'pause'): void;
  (e: 'tick-backward'): void;
  (e: 'tick-forward'): void;
  (e: 'seek-change', payload: number): void;
  (e: 'play-previous-movie'): void;
  (e: 'play-next-movie'): void;
  (e: 'set-play-speed', payload: number): void;
  (e: 'set-volume', payload: number): void;
  (e: 'playmode-change', payload: boolean): void;
}>();

const playSpeeds = [0.25, 0.5, 1.0, 2.0, 5.0];
const volumeMin = 0.0;
const volumeMax = 1.0;
const volumeStep = 0.01;
const volumeEmitDenom = 100;

const initVolumeState = (): VolumeState => {
  return {
    val: 1.0,
  };
};
const state = reactive<MoviePlayerControlAreaState>({
  volume: initVolumeState(),
});
const refSeekbarInput = ref<HTMLInputElement>();

// computed
const isViewModeFull = computed(() => {
  return props.viewMode === 'full';
});
const isViewModeComp = computed(() => {
  return props.viewMode === 'comp';
});
const isViewModeOther = computed(() => {
  return !isViewModeFull.value && !isViewModeComp.value;
});
const isSizeModeLg = computed(() => {
  return props.sizeMode === 'lg';
});
const isSizeModeGteLg = computed(() => {
  return isSizeModeLg.value;
});
const isPaused = computed(() => {
  return !props.isPlaying;
});
const myCurrentTimeDisp = computed(() => {
  // currentがまだ来てなかったらstartの方を表示しとく
  return props.currentTimeDisp || props.startTimeDisp;
});

// methods
const play = () => {
  emit('play');
};
const pause = () => {
  emit('pause');
};
const tickBackward = () => {
  emit('tick-backward');
};
const tickForward = () => {
  emit('tick-forward');
};
const emitSeekChange = (msec: number) => {
  if (!refSeekbarInput.value) return;

  const currentMSec = parseInt(refSeekbarInput.value.value);
  emit('seek-change', currentMSec + msec);
};
const seekBackwardOneSec = () => {
  emitSeekChange(-1000);
};
const seekBackwardSixtySec = () => {
  emitSeekChange(-60000);
};
const seekForwardOneSec = () => {
  emitSeekChange(1000);
};
const seekForwardSixtySec = () => {
  emitSeekChange(60000);
};
const changePlaySpeed = (speed: number) => {
  emit('set-play-speed', speed);
};
const getVolumeEmitVal = () => {
  return state.volume.val / volumeEmitDenom;
};
const volumeOff = () => {
  state.volume.val = volumeMin;
  emit('set-volume', getVolumeEmitVal());
};
const volumeUp = () => {
  state.volume.val = Math.min(
    state.volume.val + volumeStep,
    volumeMax,
  );
  emit('set-volume', getVolumeEmitVal());
};
const volumeChange = () => {
  emit('set-volume', getVolumeEmitVal());
};
const seekChange = (evt: InputEvent) => {
  if (!evt.target) return;

  emit('seek-change', parseInt((evt.target as HTMLInputElement).value));
};
const resetVolume = () => {
  state.volume.val = volumeMin;
};
const onIsPlayBackwardChange = (evt: Event) => {
  const isPlayBackward = (evt.target as HTMLInputElement).checked;
  emit('playmode-change', isPlayBackward);
};

defineExpose({ emitSeekChange, resetVolume });
</script>

<style lang="scss" scoped>
</style>
